// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import config from "@config";
import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import { getSchema } from "@utils/schema";
import {
  GetNurseDeclinedReasonsList,
  GetNurseDeclinedReason,
  GetNurseDeclinedReasonSchema,
  AddNurseDeclinedReason,
  UpdateNurseDeclinedReason,
} from "@modules/service/constants/actions";

import { ApprovalStateEnum } from "@modules/service/constants";
import type { ApprovableContainer } from "@modules/service/types";
import type { NurseDeclinedReason } from "@modules/service/reducer/nurse-declined-reasons/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type NurseDeclinedReasonListRequest = $Exact<{
  clientId: string,
  isActive?: boolean | null,
  page?: number,
}>;

export function* onGetNurseDeclinedReasonsList({
  payload,
}: Action<NurseDeclinedReasonListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<NurseDeclinedReason>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason`,
      {
        params: {
          limit: config.resultsPerPage,
          isActive: payload.isActive,
          page: payload.page || 1,
        },
      },
    );

    yield put(actionCreators.GetNurseDeclinedReasonsListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNurseDeclinedReasonsListError(e.response));
  }
}

type NurseDeclinedReasonRequest = NurseDeclinedReasonListRequest &
  $Exact<{
    reasonId: string,
  }>;

export function* onGetNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<NurseDeclinedReason>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}`,
    );

    yield put(actionCreators.GetNurseDeclinedReasonSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNurseDeclinedReasonError(e.response));
  }
}

type RefreshRequest = $Exact<{
  clientId: string,
  reasonId?: string,
  isActive?: null | boolean,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(
      actionCreators.GetNurseDeclinedReasonsList({
        clientId: params.clientId,
        isActive: params.isActive,
      }),
    );
    if (params.reasonId) {
      yield put(
        actionCreators.GetNurseDeclinedReason({
          clientId: params.clientId,
          reasonId: params.reasonId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

// CREATE

export function* onGetNurseDeclinedReasonSchema(): Generator<*, void, Object> {
  try {
    const schema = getSchema("NurseDeclineReasonRevisionDTO");

    yield put(actionCreators.GetNurseDeclinedReasonSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetNurseDeclinedReasonSchemaError(e.response));
  }
}

type NurseDeclinedReasonAddRequest = NurseDeclinedReasonListRequest &
  $Exact<{
    draft: NurseDeclinedReason,
  }>;

export function* onAddNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason`,
      payload.draft,
    );

    yield all([
      put(actionCreators.AddNurseDeclinedReasonSuccess(response.data)),
      put(actionCreators.SetShowAddNurseDeclinedReasonModal(false)),
      put(
        actionCreators.GetNurseDeclinedReasonsList({
          clientId: payload.clientId,
          isActive: payload.isActive,
        }),
      ),
      put(sendSuccessMessage("NURSE_DECLINED_REASON_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddNurseDeclinedReasonError(e.response));
  }
}

// UPDATE

type NurseDeclinedReasonUpdateRequest = NurseDeclinedReasonRequest &
  $Exact<{
    draft: NurseDeclinedReason,
  }>;

export function* onUpdateNurseDeclinedReason({
  payload,
}: Action<NurseDeclinedReasonUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reasonId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const draft = {
      ...payload.draft,
    };
    delete draft.id;
    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/nurse-declined-reason/reason/${payload.reasonId}`,
      draft,
    );

    yield all([
      put(actionCreators.UpdateNurseDeclinedReasonSuccess(response.data)),
      put(actionCreators.ResetNurseDeclinedReason()),
      put(actionCreators.SetShowEditNurseDeclinedReasonModal(false)),
      put(
        actionCreators.GetNurseDeclinedReasonsList({
          clientId: payload.clientId,
          isActive: payload.isActive,
        }),
      ),
    ]);
    yield put(sendSuccessMessage("NURSE_DECLINED_REASON_UPDATED"));
  } catch (e) {
    yield put(actionCreators.UpdateNurseDeclinedReasonError(e.response));
  }
}

// ROOT

export function* NurseDeclinedReasonListSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetNurseDeclinedReasonsList, onGetNurseDeclinedReasonsList),
    takeEvery(GetNurseDeclinedReason, onGetNurseDeclinedReason),
    takeEvery(GetNurseDeclinedReasonSchema, onGetNurseDeclinedReasonSchema),
    takeEvery(AddNurseDeclinedReason, onAddNurseDeclinedReason),
    takeEvery(UpdateNurseDeclinedReason, onUpdateNurseDeclinedReason),
  ]);
}

export default NurseDeclinedReasonListSagas;
